import React, { useState, useEffect, useCallback } from "react";
import { Card, Badge, Spinner, Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import { getDeploymentLogs, logUtilization } from "../../../services/equipmentService";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-IN") : "—");

const DeploymentLogs = () => {
 const navigate = useNavigate();

 const [logs, setLogs] = useState([]);
 const [loading, setLoading] = useState(true);
 const [statusFilter, setStatusFilter] = useState("");
 const [projectFilter, setProjectFilter] = useState("");
 const [projects, setProjects] = useState([]);
 const [page, setPage] = useState(1);
 const [pagination, setPagination] = useState({});
 const [logHoursModal, setLogHoursModal] = useState({ show: false, equipmentId: "", equipmentName: "", hours: "", remarks: "" });

 useEffect(() => {
 api.get("/projects", { params: { limit: 200 } })
 .then((res) => setProjects(res.data?.data || res.data?.projects || []))
 .catch(() => {});
 }, []);

 const fetchLogs = useCallback(async () => {
 setLoading(true);
 try {
 const params = { page, limit: 25 };
 if (statusFilter) params.status = statusFilter;
 if (projectFilter) params.project = projectFilter;

 const res = await getDeploymentLogs(params);
 setLogs(res.data?.data || []);
 setPagination(res.data?.pagination || {});
 } catch (_) {
 toast.error("Failed to load deployment logs");
 } finally {
 setLoading(false);
 }
 }, [page, statusFilter, projectFilter]);

 useEffect(() => {
 fetchLogs();
 }, [fetchLogs]);

 const handleLogHours = async () => {
 const { equipmentId, hours, remarks } = logHoursModal;
 if (!hours || parseFloat(hours) <= 0) {
 toast.error("Enter a valid number of hours");
 return;
 }
 try {
 await logUtilization(equipmentId, { hours, remarks });
 toast.success("Utilization logged");
 setLogHoursModal({ show: false, equipmentId: "", equipmentName: "", hours: "", remarks: "" });
 fetchLogs();
 } catch (err) {
 toast.error("Failed to log hours");
 }
 };

 return (
 <>
 <PageTitle activeMenu="Deployment Logs" motherMenu="Equipment" />

 <Card className="shadow-sm">
 <Card.Header className="d-flex flex-wrap align-items-center justify-content-between gap-2">
 <h5 className="mb-0">
 <i className="fas fa-history me-2 text-primary" />
 Equipment Deployment Logs
 </h5>
 <div className="d-flex gap-2 flex-wrap">
 <Form.Select
 size="sm"
 style={{ width: 160 }}
 value={projectFilter}
 onChange={(e) => { setProjectFilter(e.target.value); setPage(1); }}
 >
 <option value="">All Projects</option>
 {projects.map((p) => (
 <option key={p._id} value={p._id}>{p.projectName || p.name}</option>
 ))}
 </Form.Select>
 <Form.Select
 size="sm"
 style={{ width: 140 }}
 value={statusFilter}
 onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
 >
 <option value="">All Statuses</option>
 {["Active", "Returned", "Transferred"].map((s) => (
 <option key={s} value={s}>{s}</option>
 ))}
 </Form.Select>
 <Button size="sm" variant="outline-secondary" onClick={() => navigate("../equipment")}>
 <i className="fas fa-arrow-left me-1" />Back
 </Button>
 </div>
 </Card.Header>

 {loading ? (
 <Card.Body className="text-center py-5">
 <Spinner animation="border" variant="primary" />
 </Card.Body>
 ) : logs.length === 0 ? (
 <Card.Body className="text-center py-5 text-muted">
 <i className="fas fa-history fa-3x mb-3 d-block" />
 No deployment logs found
 </Card.Body>
 ) : (
 <div className="table-responsive">
 <table className="table table-hover align-middle mb-0">
 <thead className="table-light">
 <tr>
 <th>Equipment</th>
 <th>Project</th>
 <th>Work Order</th>
 <th>Operator</th>
 <th>Location</th>
 <th>Deployed From</th>
 <th>Returned On</th>
 <th>Hours</th>
 <th>Purpose</th>
 <th>Status</th>
 <th>Actions</th>
 </tr>
 </thead>
 <tbody>
 {logs.map((log) => (
 <tr key={log._id}>
 <td>
 <div className="fw-semibold">{log.equipment?.name || "—"}</div>
 <small className="text-muted">{log.equipment?.equipmentCode}</small>
 </td>
 <td>{log.project?.projectName || "—"}</td>
 <td>{log.workOrder?.woNo || "—"}</td>
 <td>{log.operator?.name || "—"}</td>
 <td>{log.location || "—"}</td>
 <td>{fmt(log.deployedFrom)}</td>
 <td>{log.deployedTo ? fmt(log.deployedTo) : <Badge bg="primary">Active</Badge>}</td>
 <td><strong>{log.utilizationHours || 0} h</strong></td>
 <td>{log.purpose || "—"}</td>
 <td>
 <Badge bg={log.status === "Active" ? "primary" : log.status === "Returned" ? "success" : "info"}>
 {log.status}
 </Badge>
 </td>
 <td>
 {log.status === "Active" && log.equipment?._id && (
 <Button
 size="sm"
 variant="outline-warning"
 title="Log Utilization Hours"
 onClick={() =>
 setLogHoursModal({
 show: true,
 equipmentId: log.equipment._id,
 equipmentName: log.equipment.name,
 hours: "",
 remarks: "",
 })
 }
 >
 <i className="fas fa-clock" />
 </Button>
 )}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}

 {pagination.totalPages > 1 && (
 <Card.Footer className="d-flex justify-content-between align-items-center">
 <small className="text-muted">
 {pagination.totalCount} logs · Page {pagination.currentPage} of {pagination.totalPages}
 </small>
 <div className="d-flex gap-2">
 <Button variant="outline-secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
 <Button variant="outline-secondary" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
 </div>
 </Card.Footer>
 )}
 </Card>

 {/* Log Hours Modal */}
 {logHoursModal.show && (
 <div
 className="modal d-block"
 style={{ background: "rgba(0,0,0,0.5)" }}
 onClick={() => setLogHoursModal({ ...logHoursModal, show: false })}
 >
 <div
 className="modal-dialog modal-dialog-centered"
 onClick={(e) => e.stopPropagation()}
 >
 <div className="modal-content">
 <div className="modal-header">
 <h5 className="modal-title">Log Utilization Hours</h5>
 <button
 className="btn-close"
 onClick={() => setLogHoursModal({ ...logHoursModal, show: false })}
 />
 </div>
 <div className="modal-body">
 <p className="text-muted">Equipment: <strong>{logHoursModal.equipmentName}</strong></p>
 <Form.Group className="mb-3">
 <Form.Label>Hours to Add</Form.Label>
 <Form.Control
 type="number"
 min="0.5"
 step="0.5"
 value={logHoursModal.hours}
 onChange={(e) => setLogHoursModal({ ...logHoursModal, hours: e.target.value })}
 placeholder="e.g. 8"
 />
 </Form.Group>
 <Form.Group>
 <Form.Label>Remarks (optional)</Form.Label>
 <Form.Control
 as="textarea"
 rows={2}
 value={logHoursModal.remarks}
 onChange={(e) => setLogHoursModal({ ...logHoursModal, remarks: e.target.value })}
 placeholder="Work done, notes..."
 />
 </Form.Group>
 </div>
 <div className="modal-footer">
 <Button variant="primary" onClick={handleLogHours}>Log Hours</Button>
 <Button variant="outline-secondary" onClick={() => setLogHoursModal({ ...logHoursModal, show: false })}>
 Cancel
 </Button>
 </div>
 </div>
 </div>
 </div>
 )}
 </>
 );
};

export default DeploymentLogs;
